/**
 * CreateSecretModal Component
 *
 * Modal for creating a new secret.
 *
 * SECURITY NOTES:
 * - Secret values are encrypted via tenant KMS key on backend
 * - Values are write-only (never retrieved via API)
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateSecret } from '../hooks/useCreateSecret';
import { createSecretFormSchema, type CreateSecretFormData } from '../../../lib/validators/secret';

/**
 * Generate secret code from name
 * Converts "Stripe API Key" to "stripe-api-key"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

interface CreateSecretModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * CreateSecretModal component
 */
export const CreateSecretModal: React.FC<CreateSecretModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);
  const [showValue, setShowValue] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateSecretFormData>({
    resolver: zodResolver(createSecretFormSchema),
    defaultValues: {
      secretCode: '',
      secretName: '',
      description: '',
      value: '',
    },
  });

  const watchName = watch('secretName');

  // Auto-generate secret code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('secretCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const { mutate: createSecret, isPending } = useCreateSecret(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Secret created successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        setShowValue(false);
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: CreateSecretFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Create secret DTO
    const secretData = {
      secretCode: data.secretCode,
      secretName: data.secretName,
      description: data.description || undefined,
      value: data.value,
    };

    createSecret(secretData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      setShowValue(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create Secret</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Security Note */}
          <Alert variant="info">
            Secret values are encrypted and stored securely. They can only be updated, not retrieved from the UI.
          </Alert>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Secret Name */}
          <FormField label="Secret Name" required error={errors.secretName?.message}>
            <Input
              {...register('secretName')}
              placeholder="e.g., Stripe API Key"
              error={!!errors.secretName}
            />
          </FormField>

          {/* Secret Code */}
          <FormField label="Secret Code" required error={errors.secretCode?.message}>
            <Input
              {...register('secretCode')}
              placeholder="e.g., stripe-api-key"
              error={!!errors.secretCode}
              disabled={autoGenerateCode}
            />
            <div className="mt-2 flex items-center">
              <Checkbox
                id="autoGenerateSecretCode"
                checked={autoGenerateCode}
                onChange={(e) => setAutoGenerateCode(e.target.checked)}
              />
              <label htmlFor="autoGenerateSecretCode" className="ml-2 text-sm text-gray-600">
                Auto-generate code from name
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Code is immutable after creation. Use lowercase letters, numbers, and hyphens.
            </p>
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this secret..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Secret Value */}
          <FormField
            label="Secret Value"
            required
            error={errors.value?.message}
            helperText="This value will be encrypted and can only be updated, not retrieved."
          >
            <div className="relative">
              <Input
                {...register('value')}
                type={showValue ? 'text' : 'password'}
                placeholder="Enter secret value..."
                error={!!errors.value}
                className="pr-24"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                onClick={() => setShowValue(!showValue)}
              >
                {showValue ? 'Hide' : 'Show'}
              </button>
            </div>
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Create Secret
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
